import React, { useState, useEffect, useRef } from 'react';
import {
    Plus,
    Copy,
    Calendar,
    Filter,
    ChevronDown,
    Link as LinkIcon,
    Wallet,
    TrendingUp,
    X,
    ChevronUp,
    Inbox,
    ExternalLink,
    CheckCircle,
    AlertCircle,
    Loader2,
    XCircle,
    CircleDollarSign
} from 'lucide-react';
import { API_BASE_URL, QR_BASE_URL } from '../../utils/apiConfig';
import { useTranslation } from 'react-i18next';

const NewPaymentLink = () => {
    const { t } = useTranslation();
    
    // API state
    const [transactions, setTransactions] = useState([]);
    const [starts, setStarts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
    });

    // Pagination state
    const [page, setPage] = useState(1);
    const limit = 20;

    // Date picker state
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // Period dropdown state
    const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
    const [activePeriod, setActivePeriod] = useState('today');
    const [hasSelectedPeriod, setHasSelectedPeriod] = useState(true); // Default to true to show today's data
    const [hasSelectedDateRange, setHasSelectedDateRange] = useState(false);
    
    // Status filter state
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('all');
    
    const [apiKey, setApiKey] = useState("");
    const [apiSecret, setApiSecret] = useState("");
    const [timer, setTimer] = useState("");
    const [userId, setUserId] = useState("");
    const [apiCredentialsLoading, setApiCredentialsLoading] = useState(false);
    const [showTransactionModal, setShowTransactionModal] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    // Modal state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedBlockchain, setSelectedBlockchain] = useState('USDT TRC20');
    const [showBlockchainDropdown, setShowBlockchainDropdown] = useState(false);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');

    // Create Payment Link form state
    const [createForm, setCreateForm] = useState({
        amount: '',
        currencyType: 'USDT-TRC20',
        timer: '',
        description: '',
    });
    const [createdPaymentLink, setCreatedPaymentLink] = useState(null);
    const [createLoading, setCreateLoading] = useState(false);
    const [createError, setCreateError] = useState('');

    // Blockchain options
    const blockchainOptions = [
        { id: 'USDT TRC20', name: 'USDT TRC20', icon: 'TRC20' },
        { id: 'USDT ERC20', name: 'USDT ERC20', icon: 'ERC20' },
        // { id: 'BTC', name: 'BTC', icon: 'BTC' },
        // { id: 'ETH', name: 'ETH', icon: 'ETH' }
    ];

    // Fetch transactions from API
    const fetchTransactions = async (tabType = 'all', page = 1, limit = 10, customPeriod = null) => {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
            });

            // Use customPeriod if provided, otherwise use activePeriod from state
            const periodToUse = customPeriod !== null ? customPeriod : activePeriod;

            // Add dateFilter parameter if:
            // 1. customPeriod is provided (user just selected a period), OR
            // 2. hasSelectedPeriod is true and periodToUse exists (using saved state)
            if (customPeriod !== null ? customPeriod : (hasSelectedPeriod && periodToUse)) {
                params.append('dateFilter', periodToUse);
            }

            // Add date range parameters ONLY if user selected dates
            if (hasSelectedDateRange && fromDate && fromDate.trim() !== '') {
                params.append('dateFrom', fromDate);
            }
            if (hasSelectedDateRange && toDate && toDate.trim() !== '') {
                params.append('dateTo', toDate);
            }
            
            // Add status filter if not 'all'
            if (selectedStatus && selectedStatus !== 'all') {
                params.append('status', selectedStatus);
            }

            const response = await fetch(
                `${API_BASE_URL}/transaction/payment-links?${params.toString()}`,
                {
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setTransactions(data.data.docs || []);
                setStarts((data.data.stats || []));
                setPagination({
                    page: data.data.page || 1,
                    limit: data.data.limit || 10,
                    total: data.data.total || 0,
                    totalPages: data.data.totalPages || 0
                });
            } else {
                throw new Error(data.error || 'Failed to fetch transactions');
            }
        } catch (err) {
            console.error('Error fetching transactions:', err);
            setError(err.message);
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to translate period values
    const translatePeriod = (period) => {
        const periodMap = {
            'today': t('paymentLinks.today'),
            'yesterday': t('paymentLinks.yesterday'),
            'thisWeek': t('paymentLinks.thisWeek'),
            'thisMonth': t('paymentLinks.thisMonth'),
            'thisYear': t('paymentLinks.thisYear')
        };
        return periodMap[period] || period;
    };

    // Computed stats
    const totalCount = starts?.totalCount || 0;
    const successfulCount = starts?.successfulTransactions || 0;
    const expiredCount = starts?.expiredTransactions || 0;
    const successRate = totalCount > 0 ? Math.round((successfulCount / totalCount) * 100) : 0;

    // Summary Statistics
    const summaryStats = [
        {
            icon: LinkIcon,
            title: t('paymentLinks.linksCreated'),
            value: totalCount,
            iconColor: 'text-blue-600',
            bgColor: 'bg-blue-50',
            isCurrency: false
        },
        {
            icon: CircleDollarSign,
            title: t('paymentLinks.settledAmount'),
            value: starts.successfulVolume || '0',
            iconColor: 'text-green-600',
            bgColor: 'bg-green-50',
            isCurrency: true
        },
        {
            icon: TrendingUp,
            title: t('paymentLinks.successRate'),
            value: `${successRate}%`,
            iconColor: 'text-purple-600',
            bgColor: 'bg-purple-50',
            isCurrency: false
        },
        {
            icon: CheckCircle,
            title: t('paymentLinks.successfulPayments'),
            value: successfulCount,
            iconColor: 'text-green-600',
            bgColor: 'bg-green-50',
            isCurrency: false
        },
        {
            icon: XCircle,
            title: t('paymentLinks.expired'),
            value: expiredCount,
            iconColor: 'text-gray-500',
            bgColor: 'bg-gray-100',
            isCurrency: false
        }
    ];


    const [copiedField, setCopiedField] = useState(null);

    const copyToClipboard = (text, field = null) => {
        navigator.clipboard.writeText(text).then(() => {
            if (field) {
                setCopiedField(field);
                setTimeout(() => setCopiedField(null), 2000);
            }
        }).catch(err => {
            console.error('Failed to copy: ', err);
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "confirmed": return "bg-green-100 text-green-700 border-green-300";
            case "initiated": return "bg-amber-100 text-amber-700 border-amber-300";
            case "amount confirmed": return "bg-blue-100 text-blue-700 border-blue-300";
            case "expired": return "bg-gray-100 text-gray-500 border-gray-300";
            case "failed": return "bg-red-100 text-red-600 border-red-300";
            default: return "bg-gray-100 text-gray-600 border-gray-300";
        }
    };

    const getExplorerUrl = (hash, currencyType) => {
        if (!hash) return null;
        if (currencyType && currencyType.toLowerCase().includes("trc20")) {
            return `https://tronscan.org/#/transaction/${hash}`;
        }
        return `https://etherscan.io/tx/${hash}`;
    };

    const truncateAddress = (address, start = 6, end = 4) => {
        if (!address) return '';
        if (address.length <= start + end) return address;
        return `${address.substring(0, start)}x...${address.substring(address.length - end)}`;
    };

    // Format period for display using translations
    const formatPeriodDisplay = (period) => {
        return translatePeriod(period);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        fetchTransactions('all', newPage, limit);
    };

    // Load transactions on component mount with today's filter by default
    useEffect(() => {
        fetchTransactions('all', page, limit, 'today'); // Pass 'today' as default period
    }, []);

    useEffect(() => {
        fetchApiKey();
    }, []);

    const handleApplyDateRange = () => {
        // Validate date range
        if (new Date(toDate) < new Date(fromDate)) {
            alert(t('paymentLinks.toDateMustBeAfterFromDate'));
            return;
        }
        setShowDatePicker(false);
    };

    const validateEmail = (emailValue) => {
        if (!emailValue) {
            setEmailError(t('paymentLinks.emailRequired'));
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailValue)) {
            setEmailError(t('paymentLinks.emailInvalid'));
            return false;
        }

        setEmailError('');
        return true;
    };

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);

        // Clear error when user starts typing
        if (emailError) {
            setEmailError('');
        }
    };

    // Fetch API credentials
    const fetchApiKey = async () => {
        // const currentUserId = getUserDataFromStorage();
        const currentUserId = localStorage.getItem("userId");

        if (!currentUserId) {

            return;
        }



        try {
            setApiCredentialsLoading(true);
            // Use the dedicated API credentials endpoint for secure access
            const response = await fetch(
                `${API_BASE_URL}/merchant/${currentUserId}/api-credentials`,
                {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );



            if (!response.ok) {
                throw new Error(
                    `Failed to fetch API key: ${response.status} ${response.statusText}`
                );
            }

            const data = await response.json();


            // Extract API credentials from the dedicated endpoint response
            let foundApiKey = null;
            let foundApiSecret = null;

            // The dedicated endpoint returns data in data.data structure
            if (data && data.data) {
                foundApiKey = data.data.apiKey || null;
                foundApiSecret = data.data.apiSecret || null;
            }

            let hasErrors = false;
            let errorMessages = [];

            if (foundApiKey) {
                setApiKey(foundApiKey);
            } else {
                hasErrors = true;
                errorMessages.push("API Key not found in response");
            }

            if (foundApiSecret) {
                setApiSecret(foundApiSecret);
            } else {
                hasErrors = true;
                errorMessages.push("API Secret not found in response");
            }

            if (data && data.data && data.data.transactionTimer) {
                setTimer(data.data.transactionTimer);
            }

            if (hasErrors) {
                console.error(
                    errorMessages.join(". ") + ". Check the API response structure."
                );

            }
        } catch (error) {
            console.error("Error fetching API credentials:", error);
        } finally {
            setApiCredentialsLoading(false);
        }
    };

    // Generate QR Generator URL
    const generateQRGeneratorUrl = () => {
        const apiKeyEncoded = apiKey ? btoa(apiKey) : "";
        const apiSecretEncoded = apiSecret ? btoa(apiSecret) : "";
        return `${QR_BASE_URL}/qr-generator?api_key=${apiKeyEncoded}&api_secret=${apiSecretEncoded}&timer=${timer}`;
    };

    const openQRGenerator = () => {

        if (apiKey && apiSecret) {
            const url = generateQRGeneratorUrl();

            window.open(url, "_blank");
        } else {
            console.error(
                "API credentials not available. API Key:",
                !!apiKey,
                "API Secret:",
                !!apiSecret
            );
            alert(t('paymentLinks.apiCredentialsNotAvailable'));
        }
    };


    // Create Payment Link handler
    const handleCreatePaymentLink = async () => {
        setCreateLoading(true);
        setCreateError('');
        try {
            const params = new URLSearchParams();
            params.append('currencyType', createForm.currencyType);
            if (createForm.amount) params.append('amount', createForm.amount);
            if (createForm.timer) params.append('timer', createForm.timer);
            if (createForm.description) params.append('description', createForm.description);

            const response = await fetch(
                `${API_BASE_URL}/transaction/create-payment-link?${params.toString()}`,
                {
                    method: 'POST',
                    credentials: 'include',
                }
            );
            const data = await response.json();
            if (data.success) {
                setCreatedPaymentLink(data);
            } else {
                setCreateError(data.error || data.message || t('paymentLinks.failedToCreate'));
            }
        } catch (err) {
            setCreateError(err.message || t('paymentLinks.networkError'));
        } finally {
            setCreateLoading(false);
        }
    };

    const handleCloseCreateModal = () => {
        setShowCreateModal(false);
        setCreatedPaymentLink(null);
        setCreateForm({
            amount: '',
            currencyType: 'USDT-TRC20',
            timer: '',
            description: '',
        });
        setCreateError('');
        // Refresh the payment links list
        fetchTransactions('all', 1, limit);
        setPage(1);
    };

    const handleCreateFormChange = (field, value) => {
        setCreateForm(prev => ({ ...prev, [field]: value }));
        if (createError) setCreateError('');
    };

    const handleGenerate = () => {
        // Validate email
        if (!validateEmail(email)) {
            return;
        }

        // Here you would typically call an API to generate the wallet


        // Close modal and reset form
        setShowCreateModal(false);
        setEmail('');
        setEmailError('');
        setSelectedBlockchain('USDT TRC20');
    };


    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if clicking inside the date picker dropdown or button
            const isDatePickerClick = event.target.closest('.date-picker') || event.target.closest('.date-picker-button');

            // Check if clicking inside the period dropdown or button
            const isPeriodClick = event.target.closest('.period-dropdown') || event.target.closest('.period-button');
            
            // Check if clicking inside the status dropdown or button
            const isStatusClick = event.target.closest('.status-dropdown') || event.target.closest('.status-button');

            if (!isDatePickerClick) {
                setShowDatePicker(false);
            }

            if (!isPeriodClick) {
                setShowPeriodDropdown(false);
            }
            
            if (!isStatusClick) {
                setShowStatusDropdown(false);
            }
        };

        if (showDatePicker || showPeriodDropdown || showStatusDropdown) {
            setTimeout(() => {
                document.addEventListener('mousedown', handleClickOutside);
            }, 0);

            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [showDatePicker, showPeriodDropdown, showStatusDropdown]);

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Header Section */}
            <div className="mb-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">
                            {t('paymentLinks.title')}
                        </h1>
                        <p className="text-sm text-gray-600 mt-1">
                            {t('paymentLinks.subtitle')}
                        </p>
                    </div>

                    {/* Create Button and Filters */}
                    <div className="flex flex-col items-end gap-3">
                        <button
                            onClick={() => setShowCreateModal(true)}
                            style={{
                                background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)",
                                borderRadius: "10px"
                            }}
                            className="flex items-center px-3 py-2 sm:px-4 text-white rounded-lg hover:opacity-90 transition-colors text-sm">
                            <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2" />
                            <span className="hidden sm:inline">{t('paymentLinks.createPaymentLink')}</span>
                            <span className="sm:hidden">{t('paymentLinks.create')}</span>
                        </button>

                        {/* Separate Filter Controls */}
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 w-full">
                            {/* Date Filter */}
                            <div className="relative bg-white border border-gray-300 rounded-lg shadow-sm w-full sm:w-auto">
                                <div className="relative date-picker">
                                    <button
                                        onClick={() => setShowDatePicker(!showDatePicker)}
                                        className="date-picker-button flex items-center justify-between sm:justify-start gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors w-full sm:w-auto"
                                    >
                                        <svg
                                            className="w-4 h-4 flex-shrink-0"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                            />
                                        </svg>
                                        <span className="truncate">
                                            {!isNaN(new Date(fromDate).getTime())
                                                ? new Date(fromDate).toLocaleDateString("en-US", {
                                                    month: "short",
                                                    day: "numeric",
                                                })
                                                : "DD/MM/YY"}{" "}
                                            -{" "}
                                            {!isNaN(new Date(toDate).getTime())
                                                ? new Date(toDate).toLocaleDateString("en-US", {
                                                    month: "short",
                                                    day: "numeric",
                                                })
                                                : "DD/MM/YY"}
                                        </span>
                                    </button>

                                    {showDatePicker && (
                                        <div className="absolute right-0 left-0 mt-2 w-full sm:w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
                                            <div className="space-y-4">
                                                <h3 className="text-sm font-semibold text-gray-900">
                                                    {t('paymentLinks.selectDateRange')}
                                                </h3>

                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                                            {t('paymentLinks.fromDate')}
                                                        </label>
                                                        <input
                                                            type="date"
                                                            value={fromDate}
                                                            onChange={(e) => setFromDate(e.target.value)}
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        />
                                                    </div>

                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                                            {t('paymentLinks.toDate')}
                                                        </label>
                                                        <input
                                                            type="date"
                                                            value={toDate}
                                                            onChange={(e) => setToDate(e.target.value)}
                                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
                                                    <button
                                                        onClick={() => setShowDatePicker(false)}
                                                        className="w-full sm:w-auto px-3 py-2 text-xs text-gray-600 hover:text-gray-800 transition-colors border border-gray-300 rounded-lg"
                                                    >
                                                        {t('common.cancel')}
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setShowDatePicker(false);
                                                            setHasSelectedDateRange(Boolean(fromDate || toDate));
                                                            setPage(1);
                                                            fetchTransactions("all", 1, limit);
                                                        }}
                                                        className="w-full sm:w-auto px-3 py-2 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                                    >
                                                        {t('common.apply')}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Period Filter */}
                            <div className="relative bg-white border border-gray-300 rounded-lg shadow-sm w-full sm:w-auto">
                                <div className="relative period-dropdown">
                                    <button
                                        onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                                        className="period-button flex items-center justify-between sm:justify-start gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors w-full sm:w-auto"
                                    >
                                        {formatPeriodDisplay(activePeriod)}
                                        <svg
                                            className={`w-4 h-4 transition-transform ${showPeriodDropdown ? "rotate-180" : ""
                                                }`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 9l-7 7-7-7"
                                            />
                                        </svg>
                                    </button>

                                    {showPeriodDropdown && (
                                        <div className="absolute right-0 left-0 sm:left-auto mt-2 w-full sm:w-36 bg-white border border-gray-200 rounded-2xl shadow-lg z-50">
                                            {["today", "yesterday", "thisWeek", "thisMonth", "thisYear"].map(
                                                (period, idx) => (
                                                    <button
                                                        key={period}
                                                        onClick={() => {
                                                            setActivePeriod(period);
                                                            setHasSelectedPeriod(true);
                                                            setShowPeriodDropdown(false);
                                                            setPage(1);
                                                            fetchTransactions("all", 1, limit, period);
                                                        }}
                                                        className={`w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 ${idx === 0 ? "rounded-t-2xl" : ""
                                                            } ${idx === 4 ? "rounded-b-2xl" : ""}`}
                                                    >
                                                        {translatePeriod(period)}
                                                    </button>
                                                )
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Status Filter */}
                            <div className="relative bg-white border border-gray-300 rounded-lg shadow-sm w-full sm:w-auto">
                                <div className="relative status-dropdown">
                                    <button
                                        onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                                        className="status-button flex items-center justify-between sm:justify-start gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors w-full sm:w-auto"
                                    >
                                        <Filter className="w-4 h-4 flex-shrink-0" />
                                        <span className="truncate">
                                            {selectedStatus === 'all' ? t('paymentLinks.allStatus') :
                                             selectedStatus === 'confirmed' ? t('paymentLinks.statusConfirmed') :
                                             selectedStatus === 'initiated' ? t('paymentLinks.statusAwaitingPayment') :
                                             selectedStatus === 'amount confirmed' ? t('paymentLinks.statusAmountConfirmed') :
                                             selectedStatus === 'expired' ? t('paymentLinks.statusExpired') :
                                             selectedStatus === 'failed' ? t('paymentLinks.statusFailed') :
                                             selectedStatus}
                                        </span>
                                        <svg
                                            className={`w-4 h-4 transition-transform ${showStatusDropdown ? "rotate-180" : ""
                                                }`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 9l-7 7-7-7"
                                            />
                                        </svg>
                                    </button>

                                    {showStatusDropdown && (
                                        <div className="absolute right-0 left-0 sm:left-auto mt-2 w-full sm:w-48 bg-white border border-gray-200 rounded-2xl shadow-lg z-50">
                                            {[
                                                { value: 'all', label: t('paymentLinks.allStatus') },
                                                { value: 'confirmed', label: t('paymentLinks.statusConfirmed') },
                                                { value: 'initiated', label: t('paymentLinks.statusAwaitingPayment') },
                                                { value: 'amount confirmed', label: t('paymentLinks.statusAmountConfirmed') },
                                                { value: 'expired', label: t('paymentLinks.statusExpired') },
                                                { value: 'failed', label: t('paymentLinks.statusFailed') }
                                            ].map((status, idx, arr) => (
                                                <button
                                                    key={status.value}
                                                    onClick={() => {
                                                        setSelectedStatus(status.value);
                                                        setShowStatusDropdown(false);
                                                        setPage(1);
                                                        fetchTransactions("all", 1, limit);
                                                    }}
                                                    className={`w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-50 ${
                                                        idx === 0 ? "rounded-t-2xl" : ""
                                                    } ${idx === arr.length - 1 ? "rounded-b-2xl" : ""} ${
                                                        selectedStatus === status.value ? "bg-blue-50 text-blue-600" : ""
                                                    }`}
                                                >
                                                    {status.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Reset Button */}
                            <button
                                onClick={() => {
                                    setActivePeriod("today");
                                    setHasSelectedPeriod(true);
                                    setSelectedStatus("all");
                                    setFromDate("");
                                    setToDate("");
                                    setHasSelectedDateRange(false);
                                    setShowDatePicker(false);
                                    setShowPeriodDropdown(false);
                                    setShowStatusDropdown(false);
                                    setPage(1);
                                    fetchTransactions("all", 1, limit, "today");
                                }}
                                className="flex items-center justify-center sm:justify-start gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors shadow-sm w-full sm:w-auto"
                            >
                                <svg
                                    className="w-4 h-4 flex-shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                    />
                                </svg>
                                {t('paymentLinks.reset')}
                            </button>
                        </div>

                    </div>
                </div>
            </div>

            {/* Summary Statistics Cards */}
            <div className="flex flex-wrap gap-0 bg-white rounded-2xl shadow-sm mb-6 overflow-hidden">
                {loading ? (
                    // Skeleton Loaders for Summary Cards
                    <>
                        {[...Array(5)].map((_, index) => (
                            <React.Fragment key={`skeleton-stat-${index}`}>
                                <div className="flex-1 min-w-[240px] px-4 py-4">
                                    <div className="flex items-center gap-3">
                                        {/* Icon skeleton */}
                                        <div className="bg-gray-200 rounded-lg p-2.5 flex-shrink-0 w-12 h-12 animate-pulse"></div>

                                        {/* Content skeleton */}
                                        <div className="flex-1 min-w-0">
                                            <div className="h-4 bg-gray-200 rounded animate-pulse w-24 mb-2"></div>
                                            <div className="flex items-center gap-2">
                                                <div className="h-6 bg-gray-200 rounded animate-pulse w-20"></div>
                                                <div className="h-4 bg-gray-200 rounded animate-pulse w-16"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Divider line between cards (except last) */}
                                {index < 4 && (
                                    <div className="w-px bg-gray-200 self-stretch"></div>
                                )}
                            </React.Fragment>
                        ))}
                    </>
                ) : (
                    summaryStats?.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <React.Fragment key={index}>
                                <div className="flex-1 min-w-[240px] px-4 py-4">
                                    <div className="flex items-center gap-3">
                                        {/* Icon in colored rounded square */}
                                        <div className={`${stat.bgColor} rounded-lg p-2.5 flex-shrink-0`}>
                                            <Icon className={`w-7 h-7 ${stat.iconColor}`} />
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-gray-600">{stat.title}</p>

                                            <div className="flex items-center gap-2">
                                                <p className="text-xl font-bold text-gray-800">{stat.isCurrency ? "$" : ""}{stat.value}</p>
                                            </div>
                                        </div>

                                    </div>
                                </div>

                                {/* Divider line between cards (except last) */}
                                {index < summaryStats.length - 1 && (
                                    <div className="w-px bg-gray-200 self-stretch"></div>
                                )}
                            </React.Fragment>
                        );
                    })
                )}
            </div>

            {/* Payment Links Table */}
            <div className="bg-white rounded-[25px] shadow-sm overflow-hidden">
                {loading ? (
                    <div className="overflow-x-auto p-2">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-white">
                                <tr>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">{t('paymentLinks.invoiceId')}</th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">{t('paymentLinks.transactionId')}</th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">{t('paymentLinks.amount')}</th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">{t('paymentLinks.address')}</th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">{t('paymentLinks.chain')}</th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">{t('paymentLinks.status')}</th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500 tracking-wider">{t('paymentLinks.date')}</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {[...Array(6)].map((_, i) => (
                                    <tr key={`skeleton-${i}`}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-block h-4 bg-gray-200 rounded animate-pulse w-28"></span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-block h-4 bg-gray-200 rounded animate-pulse w-32"></span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-block h-4 bg-gray-200 rounded animate-pulse w-16"></span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <span className="inline-block h-4 bg-gray-200 rounded animate-pulse w-40"></span>
                                                <span className="inline-block h-4 w-4 bg-gray-200 rounded animate-pulse"></span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-2">
                                                <span className="inline-block h-7 w-7 bg-gray-200 rounded animate-pulse"></span>
                                                <span className="inline-block h-4 bg-gray-200 rounded animate-pulse w-20"></span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-block h-6 bg-gray-200 rounded-full animate-pulse w-20"></span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-block h-4 bg-gray-200 rounded animate-pulse w-24"></span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : error ? (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
                        <div className="flex items-center">
                            <svg className="w-4 h-4 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-red-700 text-sm">{error}</span>
                        </div>
                    </div>
                ) : transactions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <Inbox className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-1">{t('paymentLinks.noDataFound')}</h3>
                        <p className="text-sm text-gray-500 max-w-md">
                            {t('paymentLinks.noDataMessage')}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto p-2">
                        <table className="min-w-full divide-y divide-gray-200" >
                            <thead className="bg-white">
                                <tr>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500  tracking-wider">
                                        {t('paymentLinks.invoiceId')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500  tracking-wider">
                                        {t('paymentLinks.transactionId')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500  tracking-wider">
                                        {t('paymentLinks.amount')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500  tracking-wider">
                                        {t('paymentLinks.address')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500  tracking-wider">
                                        {t('paymentLinks.chain')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500  tracking-wider">
                                        {t('paymentLinks.status')}
                                    </th>
                                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500  tracking-wider">
                                        {t('paymentLinks.date')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200 ">
                                {transactions?.map((transaction, index) => (
                                    <tr key={transaction._id || index} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => { setSelectedTransaction(transaction); setShowTransactionModal(true); }}>
                                        {/* Invoice ID */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center text-sm text-gray-900">
                                                {truncateAddress(transaction.invoiceNo || transaction.invoiceNo || 'N/A')}
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(transaction.invoiceNo || ''); }}
                                                    className="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                                >
                                                    <Copy className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>

                                        {/* Type */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center text-sm text-gray-900">
                                                {truncateAddress(transaction.transactionId || transaction.transactionId || 'N/A')}
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(transaction.transactionId || ''); }}
                                                    className="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                                >
                                                    <Copy className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-sm text-gray-900">${transaction.amount || 0}</span>
                                        </td>

                                        {/* Address */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center text-sm text-gray-900">
                                                {truncateAddress(transaction.walletAddress || transaction.walletAddress || '')}
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(transaction.walletAddress || ''); }}
                                                    className="ml-2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                                >
                                                    <Copy className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>

                                        {/* Chain */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <img src={transaction.currencyType === "USDT-TRC20" ? '/TRC20.png' : '/USDT-ERC20.png'} alt='chain' className='w-7 h-7 inline-block ' />
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium `}>
                                                {transaction.currencyType || transaction.currencyType || 'TRC-20'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span
                                                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium
    ${transaction?.status === "confirmed"
                                                        ? "text-green-600 bg-green-100"
                                                        : transaction?.status === "initiated"
                                                            ? "text-amber-600 bg-amber-100"
                                                            : transaction?.status === "amount confirmed"
                                                                ? "text-blue-600 bg-blue-100"
                                                                : transaction?.status === "expired"
                                                                    ? "text-gray-500 bg-gray-100"
                                                                    : transaction?.status === "failed"
                                                                        ? "text-red-600 bg-red-100"
                                                                        : "text-gray-600 bg-gray-100"
                                                    }
  `}
                                            >
                                                {transaction?.status === "confirmed" ? t('paymentLinks.statusConfirmed') :
                                                 transaction?.status === "initiated" ? t('paymentLinks.statusAwaitingPayment') :
                                                 transaction?.status === "amount confirmed" ? t('paymentLinks.statusAmountConfirmed') :
                                                 transaction?.status === "expired" ? t('paymentLinks.statusExpired') :
                                                 transaction?.status === "failed" ? t('paymentLinks.statusFailed') :
                                                 transaction?.status || "N/A"}
                                            </span>

                                        </td>

                                        {/* Created At */}
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {transaction.createdAt ? new Date(transaction.createdAt).toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                hour: 'numeric',
                                                minute: '2-digit',
                                                hour12: true
                                            }) : 'N/A'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pagination */}
            {pagination.total > 0 && (
                <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="text-sm text-gray-700">
                        <span className="hidden sm:inline">
                            {t('paymentLinks.showingResults', { from: ((pagination.page - 1) * pagination.limit) + 1, to: Math.min(pagination.page * pagination.limit, pagination.total), total: pagination.total })}
                        </span>
                        <span className="sm:hidden">
                            {t('paymentLinks.showingResultsMobile', { from: ((pagination.page - 1) * pagination.limit) + 1, to: Math.min(pagination.page * pagination.limit, pagination.total), total: pagination.total })}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => handlePageChange(page - 1)}
                            disabled={page <= 1}
                            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {t('paymentLinks.previous')}
                        </button>
                        <span className="px-3 py-1 text-sm text-gray-700">
                            {t('paymentLinks.page')} {pagination.page} {t('paymentLinks.of')} {pagination.totalPages}
                        </span>
                        <button
                            onClick={() => handlePageChange(page + 1)}
                            disabled={page >= pagination.totalPages}
                            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {t('paymentLinks.next')}
                        </button>
                    </div>
                </div>
            )}

            {/* Create Wallet Modal */}
            {/* Transaction Detail Modal */}
            {showTransactionModal && selectedTransaction && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setShowTransactionModal(false); setSelectedTransaction(null); setCopiedField(null); }}>
                    <div className="bg-[#F6F7F8] rounded-[19px] shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-900">{t('paymentLinks.transactionDetails') || 'Transaction Details'}</h2>
                            <button
                                onClick={() => { setShowTransactionModal(false); setSelectedTransaction(null); setCopiedField(null); }}
                                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X className="w-6 h-6 text-gray-500" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="p-6 space-y-4">
                            {/* Status Badge */}
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-500">Status</span>
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase border ${getStatusColor(selectedTransaction.status)}`}>
                                    {selectedTransaction.status === "confirmed" ? t('paymentLinks.statusConfirmed') :
                                     selectedTransaction.status === "initiated" ? t('paymentLinks.statusAwaitingPayment') :
                                     selectedTransaction.status === "amount confirmed" ? t('paymentLinks.statusAmountConfirmed') :
                                     selectedTransaction.status === "expired" ? t('paymentLinks.statusExpired') :
                                     selectedTransaction.status === "failed" ? t('paymentLinks.statusFailed') :
                                     selectedTransaction.status || "Unknown"}
                                </span>
                            </div>

                            {/* Invoice Number */}
                            {selectedTransaction.invoiceNo && (
                                <div className="bg-white rounded-xl p-4">
                                    <div className="text-xs text-gray-500 mb-1">Invoice Number</div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-900 break-all">{selectedTransaction.invoiceNo}</span>
                                        <button onClick={() => copyToClipboard(selectedTransaction.invoiceNo, 'invoiceNo')} className="ml-2 p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0">
                                            {copiedField === 'invoiceNo' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Transaction ID */}
                            {selectedTransaction.transactionId && (
                                <div className="bg-white rounded-xl p-4">
                                    <div className="text-xs text-gray-500 mb-1">Transaction ID</div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-900 break-all">{selectedTransaction.transactionId}</span>
                                        <button onClick={() => copyToClipboard(selectedTransaction.transactionId, 'transactionId')} className="ml-2 p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0">
                                            {copiedField === 'transactionId' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Amount, Fee, Net */}
                            <div className="bg-white rounded-xl p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500">Amount</span>
                                    <span className="text-sm font-bold text-gray-900">${selectedTransaction.amount || 0} USDT</span>
                                </div>
                                {selectedTransaction.actualBalance != null && selectedTransaction.actualBalance !== selectedTransaction.amount && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-gray-500">Actual Received</span>
                                        <span className="text-sm font-medium text-gray-900">${selectedTransaction.actualBalance} USDT</span>
                                    </div>
                                )}
                                {selectedTransaction.fee > 0 && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-gray-500">Fee</span>
                                        <span className="text-sm font-medium text-red-600">-${selectedTransaction.fee} USDT</span>
                                    </div>
                                )}
                                {selectedTransaction.netAmount != null && (
                                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                                        <span className="text-xs text-gray-500">Net Amount</span>
                                        <span className="text-sm font-bold text-green-700">${selectedTransaction.netAmount?.toFixed?.(2) || selectedTransaction.netAmount} USDT</span>
                                    </div>
                                )}
                            </div>

                            {/* Wallet Address */}
                            {selectedTransaction.walletAddress && (
                                <div className="bg-white rounded-xl p-4">
                                    <div className="text-xs text-gray-500 mb-1">Wallet Address</div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-900 break-all">{selectedTransaction.walletAddress}</span>
                                        <button onClick={() => copyToClipboard(selectedTransaction.walletAddress, 'walletAddress')} className="ml-2 p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0">
                                            {copiedField === 'walletAddress' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Currency / Chain */}
                            {selectedTransaction.currencyType && (
                                <div className="bg-white rounded-xl p-4">
                                    <div className="text-xs text-gray-500 mb-1">Network / Chain</div>
                                    <div className="flex items-center gap-2">
                                        <img src={selectedTransaction.currencyType === "USDT-TRC20" ? '/TRC20.png' : '/USDT-ERC20.png'} alt='chain' className='w-5 h-5' />
                                        <span className="text-sm font-medium text-gray-900">{selectedTransaction.currencyType}</span>
                                    </div>
                                </div>
                            )}

                            {/* Blockchain TX Hash */}
                            {selectedTransaction.blockchainTxHash && (
                                <div className="bg-white rounded-xl p-4">
                                    <div className="text-xs text-gray-500 mb-1">Blockchain TX Hash</div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-900 break-all">{selectedTransaction.blockchainTxHash}</span>
                                        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                                            <button onClick={() => copyToClipboard(selectedTransaction.blockchainTxHash, 'txHash')} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                                {copiedField === 'txHash' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                                            </button>
                                            <a href={getExplorerUrl(selectedTransaction.blockchainTxHash, selectedTransaction.currencyType)} target="_blank" rel="noopener noreferrer" className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                                <ExternalLink className="w-4 h-4" />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Created Date */}
                            <div className="bg-white rounded-xl p-4">
                                <div className="text-xs text-gray-500 mb-1">Created</div>
                                <span className="text-sm font-medium text-gray-900">
                                    {selectedTransaction.createdAt ? new Date(selectedTransaction.createdAt).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true
                                    }) : 'N/A'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Payment Link Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={handleCloseCreateModal}>
                    <div className="bg-[#F6F7F8] rounded-2xl shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-6 pb-4">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    {createdPaymentLink ? t('paymentLinks.paymentLinkCreated') : t('paymentLinks.createPaymentLink')}
                                </h2>
                                {!createdPaymentLink && (
                                    <p className="text-sm text-gray-500 mt-0.5">{t('paymentLinks.createForm.subtitle')}</p>
                                )}
                            </div>
                            <button
                                onClick={handleCloseCreateModal}
                                className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        {createdPaymentLink ? (
                            /* ===== SUCCESS VIEW ===== */
                            <div className="p-6 pt-2 space-y-4">
                                {/* Success Icon */}
                                <div className="flex flex-col items-center py-4">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-3">
                                        <CheckCircle className="w-8 h-8 text-green-600" />
                                    </div>
                                    <p className="text-lg font-semibold text-gray-900">{t('paymentLinks.paymentLinkCreated')}</p>
                                </div>

                                {/* Payment URL */}
                                <div className="bg-white rounded-xl p-4">
                                    <div className="text-xs text-gray-500 mb-1">{t('paymentLinks.paymentUrl')}</div>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-medium text-blue-600 break-all">{createdPaymentLink.url}</span>
                                        <button
                                            onClick={() => copyToClipboard(createdPaymentLink.url, 'paymentUrl')}
                                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                                        >
                                            {copiedField === 'paymentUrl' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                {/* Transaction ID */}
                                {createdPaymentLink.transactionId && (
                                    <div className="bg-white rounded-xl p-4">
                                        <div className="text-xs text-gray-500 mb-1">{t('paymentLinks.transactionId')}</div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-900 break-all">{createdPaymentLink.transactionId}</span>
                                            <button
                                                onClick={() => copyToClipboard(createdPaymentLink.transactionId, 'createdTxId')}
                                                className="ml-2 p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
                                            >
                                                {copiedField === 'createdTxId' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Invoice No */}
                                {createdPaymentLink.invoiceNo && (
                                    <div className="bg-white rounded-xl p-4">
                                        <div className="text-xs text-gray-500 mb-1">{t('paymentLinks.invoiceId')}</div>
                                        <span className="text-sm font-medium text-gray-900">{createdPaymentLink.invoiceNo}</span>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="space-y-3 pt-2">
                                    <button
                                        onClick={() => window.open(createdPaymentLink.url, '_blank')}
                                        style={{
                                            background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)",
                                            borderRadius: "10px"
                                        }}
                                        className="w-full flex items-center justify-center gap-2 py-3 text-white font-medium hover:opacity-90 transition-colors"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                        {t('paymentLinks.openPaymentPage')}
                                    </button>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => {
                                                setCreatedPaymentLink(null);
                                                setCreateForm({
                                                    amount: '',
                                                    currencyType: 'USDT-TRC20',
                                                    timer: '',
                                                    description: '',
                                                });
                                                setCreateError('');
                                            }}
                                            className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-[10px] font-medium hover:bg-gray-50 transition-colors text-sm"
                                        >
                                            {t('paymentLinks.createAnother')}
                                        </button>
                                        <button
                                            onClick={handleCloseCreateModal}
                                            className="flex-1 py-2.5 bg-gray-900 text-white rounded-[10px] font-medium hover:bg-gray-800 transition-colors text-sm"
                                        >
                                            {t('paymentLinks.done')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* ===== FORM VIEW ===== */
                            <div className="px-6 pb-6 pt-2 space-y-5">
                                {/* Error Display */}
                                {createError && (
                                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                        <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                        <span className="text-sm text-red-700">{createError}</span>
                                    </div>
                                )}

                                {/* Row 1: Amount + Currency */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="text-sm font-medium text-gray-700">{t('paymentLinks.createForm.amount')}</label>
                                        <div className="relative">
                                            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">$</span>
                                            <input
                                                type="number"
                                                min="0"
                                                step="any"
                                                value={createForm.amount}
                                                onChange={(e) => handleCreateFormChange('amount', e.target.value)}
                                                placeholder={t('paymentLinks.createForm.amountPlaceholder')}
                                                className="w-full pl-7 pr-4 py-2.5 rounded-xl bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400 text-sm"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-sm font-medium text-gray-700">{t('paymentLinks.createForm.currency')}</label>
                                        <select
                                            value={createForm.currencyType}
                                            onChange={(e) => handleCreateFormChange('currencyType', e.target.value)}
                                            className="w-full px-4 py-2.5 rounded-xl bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 text-sm appearance-none cursor-pointer"
                                            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M2 4l4 4 4-4'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center' }}
                                        >
                                            <option value="USDT-TRC20">USDT TRC20</option>
                                            <option value="USDT-ERC20">USDT ERC20</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Row 2: Timer + Description */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="text-sm font-medium text-gray-700">{t('paymentLinks.createForm.timer')}</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="2880"
                                            value={createForm.timer}
                                            onChange={(e) => handleCreateFormChange('timer', e.target.value)}
                                            placeholder={timer ? t('paymentLinks.createForm.timerPlaceholder', { timer }) : '30'}
                                            className="w-full px-4 py-2.5 rounded-xl bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400 text-sm"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-sm font-medium text-gray-700">{t('paymentLinks.createForm.description')}</label>
                                        <input
                                            type="text"
                                            value={createForm.description}
                                            onChange={(e) => handleCreateFormChange('description', e.target.value)}
                                            placeholder={t('paymentLinks.createForm.descriptionPlaceholder')}
                                            className="w-full px-4 py-2.5 rounded-xl bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 placeholder-gray-400 text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <button
                                    onClick={handleCreatePaymentLink}
                                    disabled={createLoading}
                                    style={{
                                        background: createLoading
                                            ? '#9CA3AF'
                                            : "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)",
                                        borderRadius: "10px"
                                    }}
                                    className="w-full flex items-center justify-center gap-2 py-3 text-white font-medium hover:opacity-90 transition-colors disabled:cursor-not-allowed mt-2"
                                >
                                    {createLoading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            {t('paymentLinks.createForm.creating')}
                                        </>
                                    ) : (
                                        <>
                                            <LinkIcon className="w-4 h-4" />
                                            {t('paymentLinks.createPaymentLink')}
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}


        </div>
    );
};

export default NewPaymentLink;